import { useState } from 'react';
import ContextPassage from '../shared/ContextPassage';
import MultipleChoiceOption from '../shared/MultipleChoiceOption';

/**
 * Stage 2.5 — Recognition MCQ (Word → Definition)
 *
 * Shows the target word prominently with a context sentence hint,
 * and four definition options. Student picks one. Feedback reveals
 * the correct answer (green check) plus an X on the wrong pick if
 * they missed.
 *
 * onSubmit returns { correct, correct_choice_id, ... } from the backend.
 */
export default function Stage2_5Recognition({
  lemma,
  contextHint,
  options,
  onSubmit,
  // Controlled props (used by OnboardingActivity so the student's pick +
  // the AI-graded result mirror to the teacher's observer view via
  // progress_state). When omitted, internal state is used.
  selected: controlledSelected,
  result: controlledResult,
  onSelect,
  onResult,
}) {
  const [localSelected, setLocalSelected] = useState(null);
  const [localResult, setLocalResult] = useState(null);
  // result: null | { correct: bool, correctId: string, selectedId: string }

  const selected =
    controlledSelected !== undefined ? controlledSelected : localSelected;
  const result = controlledResult !== undefined ? controlledResult : localResult;

  const handleSelect = async (choiceId) => {
    if (result) return;
    if (typeof onSelect === 'function') onSelect(choiceId);
    else setLocalSelected(choiceId);

    const data = await onSubmit(choiceId);
    const next = {
      correct: data?.correct !== false,
      correctId: data?.correct_choice_id,
      selectedId: choiceId,
    };
    if (typeof onResult === 'function') onResult(next);
    else setLocalResult(next);
  };

  return (
    <div className="space-y-8 max-w-lg mx-auto">
      <div className="text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white font-serif">
          {lemma}
        </h2>
      </div>

      <ContextPassage text={contextHint} highlight={lemma} />

      <div className="space-y-3">
        {options.map((option) => {
          const isSelected = selected === option.id;
          const isCorrectOption = result && result.correctId === option.id;
          const isWrongSelection =
            result && !result.correct && isSelected && !isCorrectOption;

          let state = 'default';
          if (isCorrectOption && isSelected) state = 'student_correct';
          else if (isCorrectOption) state = 'reveal_correct';
          else if (isWrongSelection) state = 'student_wrong';
          else if (result && !isSelected) state = 'unselected';

          return (
            <MultipleChoiceOption
              key={option.id}
              state={state}
              layout="row"
              letter={option.id.toUpperCase()}
              text={option.text}
              onClick={() => handleSelect(option.id)}
              disabled={!!result}
              focusKey={`stage2_5-option-${option.id}`}
            />
          );
        })}
      </div>
    </div>
  );
}

